import { useMemo, useCallback, useState, useRef } from "react";
import { XAxisTooltipValue } from "../types";

interface ChartContainerOffset {
  left: number;
  top: number;
}

/**
 * Manages overtime chart tooltip state using React state patterns
 * Handles hover, persistent (fixed), and find-in-viz tooltip behaviors
 */
/** A series data point in the tooltip */
interface TooltipSeriesPoint {
  year: number;
  tradeValueMin: number;
  tradeValueMax: number;
  pointMin: number;
  pointMax: number;
  properties: Record<string, unknown>;
}

/** Mapped series data reference item */
interface MappedSeriesRefItem {
  seriesKey: string;
  mappedSeries: TooltipSeriesPoint[];
  useId?: string;
}

export const useOvertimeTooltip = (
  highlightedItem: string | undefined,
  mappedSeriesDataRef: React.MutableRefObject<MappedSeriesRefItem[]>,
  clearHighlightedItemCallback?: () => void,
) => {
  const [hoveredSeries, setHoveredSeries] = useState<{
    series: TooltipSeriesPoint[];
    fillColor: string;
  } | null>(null);

  // Use ref to avoid recreating callbacks when hoveredSeries changes
  const hoveredSeriesRef = useRef(hoveredSeries);
  hoveredSeriesRef.current = hoveredSeries;

  const [isTooltipPersistent, setIsTooltipPersistent] = useState(false);
  const [suppressTooltip, setSuppressTooltip] = useState(false);

  // Refs for stable callbacks
  const isTooltipPersistentRef = useRef(isTooltipPersistent);
  isTooltipPersistentRef.current = isTooltipPersistent;
  const suppressTooltipRef = useRef(suppressTooltip);
  suppressTooltipRef.current = suppressTooltip;
  const highlightedItemRef = useRef(highlightedItem);
  highlightedItemRef.current = highlightedItem;

  const [tooltipDisplay, setTooltipDisplay] = useState<{
    xAxisValue: XAxisTooltipValue | null;
    contentData: Record<string, unknown>;
    fillColor: string;
    yAxisMarkerExtent: { min: number; max: number } | null;
  } | null>(null);

  const [chartContainerOffset, setChartContainerOffset] = useState<
    ChartContainerOffset | undefined
  >(undefined);

  const onShowTooltip = useCallback(
    (data: { series: TooltipSeriesPoint[]; fillColor: string }) => {
      setHoveredSeries(data);
      setSuppressTooltip(false);
    },
    [],
  );

  const onHideTooltip = useCallback(() => {
    if (isTooltipPersistentRef.current || highlightedItemRef.current) {
      return;
    }
    setHoveredSeries(null);
    setTooltipDisplay(null);
    setSuppressTooltip(false);
  }, []);

  const setXAxisTooltipValue = useCallback(
    (value: XAxisTooltipValue | null) => {
      if (!value || !value.year) {
        setTooltipDisplay(null);
        return;
      }

      if (suppressTooltipRef.current) {
        return;
      }

      let dataToUse = hoveredSeriesRef.current;

      // Show tooltip only for highlighted series if one is selected
      if (
        highlightedItemRef.current &&
        mappedSeriesDataRef.current.length > 0
      ) {
        const highlightedMappedSeries = mappedSeriesDataRef.current.find(
          (s: MappedSeriesRefItem) => s.seriesKey === highlightedItemRef.current,
        );

        if (highlightedMappedSeries) {
          dataToUse = {
            series: highlightedMappedSeries.mappedSeries,
            fillColor:
              highlightedMappedSeries.useId || highlightedItemRef.current,
          };
        } else {
          setTooltipDisplay(null);
          return;
        }
      }

      if (!dataToUse) {
        setTooltipDisplay(null);
        return;
      }

      const { series, fillColor } = dataToUse;
      const filteredSeries = series.find((d: TooltipSeriesPoint) => d.year === value.year);

      if (filteredSeries) {
        const { pointMin, pointMax } = filteredSeries;
        const mappedDatum = {
          year: filteredSeries.year,
          ...filteredSeries.properties,
        };

        setTooltipDisplay({
          xAxisValue: value,
          contentData: mappedDatum,
          fillColor,
          yAxisMarkerExtent: { min: pointMin, max: pointMax },
        });
      } else {
        setTooltipDisplay(null);
      }
    },
    [mappedSeriesDataRef],
  );

  const snapTooltipToCoordinates = useMemo((): [number, number] | undefined => {
    if (
      !tooltipDisplay?.xAxisValue ||
      !chartContainerOffset ||
      !tooltipDisplay.yAxisMarkerExtent
    ) {
      return undefined;
    }

    const { x } = tooltipDisplay.xAxisValue;
    const { max } = tooltipDisplay.yAxisMarkerExtent;
    const { left, top } = chartContainerOffset;

    return [x + left, top + max];
  }, [tooltipDisplay, chartContainerOffset]);

  const updateChartContainerOffset = useCallback((node: Element | null) => {
    if (!node) return;

    const { left, top } = node.getBoundingClientRect();

    setChartContainerOffset((prev) => {
      if (
        !prev ||
        Math.abs(prev.left - left) > 0.5 ||
        Math.abs(prev.top - top) > 0.5
      ) {
        return { left, top };
      }
      return prev;
    });
  }, []);

  // Store tooltipDisplay in a ref for stable callback
  const tooltipDisplayRef = useRef(tooltipDisplay);
  tooltipDisplayRef.current = tooltipDisplay;

  const clearHighlightedItem = useCallback(() => {
    if (
      !isTooltipPersistentRef.current &&
      tooltipDisplayRef.current &&
      !highlightedItemRef.current
    ) {
      // Enter persistent mode on first click
      setIsTooltipPersistent(true);
    } else {
      // Exit persistent mode and clear
      setIsTooltipPersistent(false);
      setTooltipDisplay(null);
      setSuppressTooltip(true);

      // Clear suppression after brief delay to allow tooltip on next mouse move
      setTimeout(() => {
        setSuppressTooltip(false);
      }, 50);

      if (clearHighlightedItemCallback) {
        clearHighlightedItemCallback();
      }
    }
  }, [clearHighlightedItemCallback]);

  return {
    tooltipDisplay,
    chartContainerOffset,
    snapTooltipToCoordinates,
    onShowTooltip,
    onHideTooltip,
    setXAxisTooltipValue,
    updateChartContainerOffset,
    clearHighlightedItem,
  };
};
